import React, { useEffect, useState , useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  ButtonGroup,
  CircularProgress,
  Stack
} from "@mui/material";

import {
  CalendarToday,
  AccessTime,
  Search,
  TrendingUp,
  MedicalServices,
  HowToReg,
  Send ,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";


import Swal from "sweetalert2";
import axios from "axios";
import { getTodayAppointments } from "../BaseURL";
import MedicalHistoryDialog from "./MedicalHistoryDialog";
import { getAttendanceStats, getDiagnosisStats ,loadPharmacyPatientsChart ,loadPharmacyPatientsMedcount  } from "../BaseURL";

// Shared design tokens — local to this file, match the records dashboard.
const PRIMARY      = '#059669';
const PRIMARY_DARK = '#047857';
const PRIMARY_BG   = 'rgba(5,150,105,0.08)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX     = {
  fontSize: '0.66rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

const Home = () => {
  // -----------------------------
  // STATE MANAGEMENT
  // -----------------------------
  const [currentUser, setCurrentUser] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [appointmentsPage, setAppointmentsPage] = useState(0);
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [appointmentDateFilter, setAppointmentDateFilter] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [liveDateTime, setLiveDateTime] = useState("");

  // Medical Records dialog
  const [medHistoryOpen, setMedHistoryOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [statCards, setStatCards] = useState([]);

  // Attendance (Chart) Period
  const [selectedPeriod, setSelectedPeriod] = useState("Today");
  const [selectedPeriod2, setSelectedPeriod2] = useState("Today");

  // Loading states
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);

  const rowsPerPage = 5;

  // Color palette for chart bars
  const chartColors = [
    "#4caf50", // Green
    "#2196f3", // Blue
    "#ff9800", // Orange
    "#9c27b0", // Purple
    "#f44336", // Red
    "#00bcd4", // Cyan
    "#ffeb3b", // Yellow
    "#e91e63"  // Pink
  ];

  // -----------------------------
  // ATTENDANCE DATA
  // -----------------------------
  const [attendanceDataByPeriod, setAttendanceDataByPeriod] = useState({
    Today: [],
    Week: [],
    Month: [],
    Year: []
  });

  const attendanceList = attendanceDataByPeriod[selectedPeriod] || [];
  const maxValue = Math.max(...attendanceList.map(m => m.value), 1);

  // -----------------------------
  // DIAGNOSIS DATA
  // -----------------------------
  const [diagnosisDataByPeriod, setDiagnosisDataByPeriod] = useState({
    Today: [],
    Week: [],
    Month: [],
    Year: []
  });

  // -----------------------------
  // DATE RANGE HELPER
  // -----------------------------
  const getDateRangeText = (period) => {
    const today = new Date();
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    };

    switch (period) {
      case "Today":
        return formatDate(today);
      
      case "Week": {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 6);
        return `${formatDate(weekAgo)} - ${formatDate(today)}`;
      }
      
      case "Month": {
        const monthAgo = new Date(today);
        monthAgo.setDate(today.getDate() - 29);
        return `${formatDate(monthAgo)} - ${formatDate(today)}`;
      }
      
      case "Year": {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} - ${formatDate(today)}`;
      }
      
      default:
        return formatDate(today);
    }
  };

  const loadAttendanceData = async () => {
    setAttendanceLoading(true);
    try {
      const periods = {
        Today: "today",
        Week: "week",
        Month: "month",
        Year: "year"
      };

      let newData = {};

      for (const key of Object.keys(periods)) {
        const res = await axios.get(getAttendanceStats, {
          params: { period: periods[key] }
        });

        console.log(res.data)

        newData[key] = res.data.success ? res.data.data : [];
      }

      setAttendanceDataByPeriod(newData);

    } catch (err) {
      console.error("Error loading attendance data:", err);
      setAttendanceDataByPeriod({
        Today: [],
        Week: [],
        Month: [],
        Year: []
      });
    } finally {
      setAttendanceLoading(false);
    }
  };

  const loadDiagnosisData = async () => {
    setDiagnosisLoading(true);
    try {
      const periods = {
        Today: "today",
        Week: "week",
        Month: "month",
        Year: "year"
      };

      let newData = {};

      for (const key of Object.keys(periods)) {
        const res = await axios.get(getDiagnosisStats, {
          params: { period: periods[key] }
        });

        newData[key] = res.data.success ? res.data.data : [];
      }

      setDiagnosisDataByPeriod(newData);

    } catch (err) {
      console.error("Error loading diagnosis data:", err);
      setDiagnosisDataByPeriod({
        Today: [],
        Week: [],
        Month: [],
        Year: []
      });
    } finally {
      setDiagnosisLoading(false);
    }
  };

 const [prescriptionData, setPrescriptionData] = useState([]);
  const filterPeriods = ['Today', 'Week', 'Month', 'Year', 'All'];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

    const getFilteredData = useMemo(() => {
    console.log('Processing prescription data, total items:', prescriptionData.length);
    
    // Since backend already filters by period, we just need to process the data
    // No need for date filtering here - backend handles it
    
    if (prescriptionData.length === 0) {
      console.log('No prescription data available');
      return [];
    }
  
    // Log sample data to understand the format
    console.log('Sample prescription item:', prescriptionData[0]);
    
    // Group medications by name and sum quantities
    const grouped = prescriptionData.reduce((acc, item) => {
      const existingItem = acc.find(med => med.name === item.name);
      if (existingItem) {
        existingItem.value += item.quantity;
      } else {
        acc.push({
          name: item.name,
          value: item.quantity,
          color: item.color
        });
      }
      return acc;
    }, []);
    
    console.log('Grouped prescription data:', grouped.length, 'unique medications for period:', selectedPeriod);
    
    return grouped
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [selectedPeriod, prescriptionData]);

  const topMedications = getFilteredData;
  const maxValuee = topMedications.length > 0 ? Math.max(...topMedications.map(med => med.value)) : 1;

  const grouped = prescriptionData.reduce((acc, item) => {
    const existingItem = acc.find(med => med.name === item.name);
    if (existingItem) {
      existingItem.value += item.quantity;
    } else {
      acc.push({
        name: item.name,
        value: item.quantity,
        color: item.color
      });
    }
    return acc;
  }, []);


  const [loading, setLoading] = useState(false);
  
  const loadAnalysischart = async () => {
    try {
      console.log('Loading analysis chart data for period:', selectedPeriod);
      
      // Convert selectedPeriod to backend format
      const periodMap = {
        'Today': 'today',
        'This Week': 'week', 
        'This Month': 'month',
        'This Year': 'year',
        'All': 'all'
      };
      
      const period = periodMap[selectedPeriod] || 'month';
      const response = await axios.get(`${loadPharmacyPatientsChart}?period=${period}&limit=25`);
      
      console.log('Analysis chart response:', response.data);
      
      if (response.data.success) {
        setPrescriptionData(response.data.results);
        console.log('Prescription data loaded:', response.data.results.length, 'items');
      } else {
        console.log('API returned success: false', response.data);
      }
    } catch (error) {
      console.error('Error loading analysis chart:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Chart Data',
        icon: 'error'
      });
    }
  };
  
    const iconMap = {
      'ScheduleIcon': ScheduleIcon,
      'CheckCircle': CheckCircleIcon,
      'WarningIcon': WarningIcon,
      'AssignmentIcon': AssignmentIcon,
      // Add more icons as needed
    };
  
    const loadAnalysisInfor = async () => {
      try {
        console.log('Loading analysis info...');
        const response = await axios.get(loadPharmacyPatientsMedcount);
        console.log('Analysis info response:', response.data);
        
        if (response.data.success) {
          setStatCards(response.data.results);
          console.log('Stat cards loaded:', response.data.results.length, 'cards');
        }
      } catch (error) {
        console.error('Error loading analysis info:', error);
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || 'Failed to Load Statistics',
          icon: 'error'
        });
      }
    };
  
      useEffect(() => {
      loadAnalysischart();
    }, [selectedPeriod]);

    useEffect(() => {
        const loadInitialData = async () => {
          console.log('Loading initial data...');
          setLoading(true);
          try {
            await Promise.all([
              loadAnalysisInfor(),
            // loadAnalysischart()
            ]);
            console.log('Initial data loading completed');
          } catch (error) {
            console.error('Error loading initial data:', error);
          } finally {
            setLoading(false);
          }
        };
        
        loadInitialData();
      }, []);
      
  // -----------------------------
  // LOAD USER
  // -----------------------------
  useEffect(() => {
    const savedUserInfo = localStorage.getItem("userInfo");
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      setCurrentUser(userInfo.name || "");
    }

    loadAttendanceData();
    loadDiagnosisData();
  }, []);

  // -----------------------------
  // LOAD TODAY APPOINTMENTS
  // -----------------------------
  const loadTodayAppointments = async () => {
    try {
      const res = await axios.get(getTodayAppointments, {
        params: { createdBy: currentUser }
      });

      if (res.data.success) {
        const normalized = res.data.appointments.map((apt) => ({
          ...apt,
          date: new Date(apt.date).toISOString().split("T")[0]
        }));

        setAppointments(normalized);
        setAppointmentDateFilter(new Date().toISOString().split("T")[0]);
      }
    } catch (error) {
      console.log("Error loading appointments:", error);
    }
  };

  // Load appointments when doctor is known
  useEffect(() => {
    if (currentUser) loadTodayAppointments();
  }, [currentUser]);

  // -----------------------------
  // LIVE CLOCK
  // -----------------------------
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setLiveDateTime(
        now.toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        })
      );
    };

    updateClock();
    const timer = setInterval(updateClock, 1000);

    return () => clearInterval(timer);
  }, []);

  // -----------------------------
  // FILTER APPOINTMENTS
  // -----------------------------
  const filteredAppointments = appointments.filter((apt) => {
    const matchesName =
      apt.patient_name.toLowerCase().includes(appointmentSearch.toLowerCase()) ||
      apt.patient_id.toLowerCase().includes(appointmentSearch.toLowerCase());

    const matchesDate =
      !appointmentDateFilter || apt.date === appointmentDateFilter;

    return matchesName && matchesDate;
  });

  // -----------------------------
  // ACTION HANDLERS
  // -----------------------------
  const handleViewMedicalRecords = (apt) => {
    setSelectedPatient(apt);
    setTimeout(() => setMedHistoryOpen(true), 0);
  };

  const handleSendToRecords = (apt) => {
    console.log("Send to Records:", apt);
  };

  const handleSendMessage = (apt) => {
    window.open(`https://wa.me/${apt.patient_tel}`, "_blank");
  };

  // -----------------------------
  // RENDER UI
  // -----------------------------
  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        bgcolor: '#f8fafc',
        minHeight: "100vh"
      }}
    >
      {/* HEADER */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Box>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.5 }}>
            General Medical · Doctor's Workspace
          </Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Welcome, Dr. {currentUser || '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Your day at a glance — appointments, attendance, and prescribing trends.
          </Typography>
        </Box>

        {/* LIVE CLOCK */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5, py: 0.75,
            borderRadius: 999,
            bgcolor: PRIMARY_BG,
            border: `1px solid rgba(5,150,105,0.25)`,
          }}
        >
          <Box
            sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: PRIMARY,
              animation: 'pulse 1.5s infinite',
              boxShadow: `0 0 0 0 ${PRIMARY}`,
            }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: PRIMARY_DARK, fontVariantNumeric: 'tabular-nums' }}>
            {liveDateTime}
          </Typography>
        </Box>
      </Box>

      {/* MAIN CONTENT */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* APPOINTMENTS */}
        <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <CardContent sx={{ p: 2.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <CalendarToday sx={{ color: PRIMARY, fontSize: 16 }} />
                  <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
                    Today's Schedule
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: STRONG }}>
                  Appointments
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: MUTED, mt: 0.25 }}>
                  {filteredAppointments.length} appointment{filteredAppointments.length === 1 ? '' : 's'} scheduled
                </Typography>
              </Box>

              <Chip
                label={`${filteredAppointments.length} total`}
                size="small"
                sx={{
                  height: 24, fontSize: '0.72rem', fontWeight: 700,
                  bgcolor: PRIMARY_BG, color: PRIMARY_DARK,
                  border: `1px solid rgba(5,150,105,0.25)`,
                }}
              />
            </Box>

            {/* SEARCH + DATE FILTER */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }} columns={{ xs: 4, sm: 8, md: 12 }}>
              <Grid item xs={12} md={6} size={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search appointments…"
                  value={appointmentSearch}
                  onChange={(e) => setAppointmentSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: MUTED }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1.5, bgcolor: '#fff' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} size={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Filter by Date"
                  type="date"
                  value={appointmentDateFilter}
                  onChange={(e) => setAppointmentDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { borderRadius: 1.5, bgcolor: '#fff' } }}
                />
              </Grid>
            </Grid>

            {/* APPOINTMENT TABLE */}
            <TableContainer
              sx={{
                borderRadius: 1.5,
                border: `1px solid ${BORDER}`,
                '& .MuiTableCell-root': { fontSize: '0.82rem', py: 1, borderBottom: `1px solid #f1f5f9` },
                '& .MuiTableCell-head': { ...LABEL_SX, color: MUTED, bgcolor: '#f8fafc', py: 1.25, borderBottom: `1px solid ${BORDER}` },
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Tel</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredAppointments
                    .slice(
                      appointmentsPage * rowsPerPage,
                      appointmentsPage * rowsPerPage + rowsPerPage
                    )
                    .map((apt) => (
                      <TableRow key={apt.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <AccessTime sx={{ fontSize: 14, color: MUTED }} />
                            <Typography sx={{ fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums', fontSize: '0.82rem' }}>
                              {apt.time}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 600, color: STRONG, fontSize: '0.82rem' }}>
                            {apt.patient_name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                            {apt.patient_id}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{apt.patient_tel}</TableCell>
                        <TableCell>
                          <Chip
                            label={apt.type}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }}
                          />
                        </TableCell>

                        <TableCell>
                          {(() => {
                            const tone =
                              apt.status === 'Waiting'  ? { bg: 'rgba(234,179,8,0.10)', fg: '#a16207', bd: 'rgba(234,179,8,0.35)' } :
                              apt.status === 'Confirmed'? { bg: PRIMARY_BG,             fg: PRIMARY_DARK, bd: 'rgba(5,150,105,0.30)' } :
                                                          { bg: '#f1f5f9',              fg: '#475569', bd: '#e2e8f0' };
                            return (
                              <Chip
                                label={apt.status}
                                size="small"
                                sx={{
                                  height: 20, fontSize: '0.7rem', fontWeight: 700,
                                  bgcolor: tone.bg, color: tone.fg,
                                  border: `1px solid ${tone.bd}`,
                                }}
                              />
                            );
                          })()}
                        </TableCell>

                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Medical Records" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleViewMedicalRecords(apt)}
                                sx={{ bgcolor: '#f0f9ff', color: '#0ea5e9', borderRadius: 1.5, p: 0.65, '&:hover': { bgcolor: '#e0f2fe', color: '#0284c7' } }}
                              >
                                <MedicalServices sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Send to Records" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleSendToRecords(apt)}
                                sx={{ bgcolor: 'rgba(234,179,8,0.10)', color: '#a16207', borderRadius: 1.5, p: 0.65, '&:hover': { bgcolor: 'rgba(234,179,8,0.18)' } }}
                              >
                                <HowToReg sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Send WhatsApp Message" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleSendMessage(apt)}
                                sx={{ bgcolor: PRIMARY_BG, color: PRIMARY_DARK, borderRadius: 1.5, p: 0.65, '&:hover': { bgcolor: 'rgba(5,150,105,0.18)' } }}
                              >
                                <Send sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}
            <TablePagination
              component="div"
              count={filteredAppointments.length}
              page={appointmentsPage}
              onPageChange={(e, newPage) => setAppointmentsPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5]}
            />
          </CardContent>
        </Card>

        {/* CHARTS */}
        <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }} sx={{ mt: 2 }}>
          
          {/* DIAGNOSIS CHART */}
          <Grid item xs={12} md={6} size={6}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2,
                bgcolor: '#fff',
                border: `1px solid ${BORDER}`,
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                {/* HEADER */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 2.25, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                      <TrendingUp sx={{ color: PRIMARY, fontSize: 16 }} />
                      <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
                        Diagnosis
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: STRONG }}>
                      Top 10 Diagnoses
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.25 }}>
                      {getDateRangeText(selectedPeriod2)}
                    </Typography>
                  </Box>

                  {/* PERIOD FILTER PILLS */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {['Today', 'Week', 'Month', 'Year'].map((period) => {
                      const active = selectedPeriod2 === period;
                      return (
                        <Button
                          key={period}
                          onClick={() => setSelectedPeriod2(period)}
                          disableElevation
                          size="small"
                          sx={{
                            px: 1.25, py: 0.4,
                            fontSize: '0.74rem', fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 999,
                            border: '1px solid',
                            borderColor: active ? PRIMARY : BORDER,
                            bgcolor: active ? PRIMARY : '#fff',
                            color: active ? '#fff' : '#475569',
                            '&:hover': { bgcolor: active ? PRIMARY_DARK : '#f1f5f9', borderColor: active ? PRIMARY_DARK : '#cbd5e1' },
                          }}
                        >
                          {period}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* LOADING STATE */}
                {diagnosisLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress size={28} sx={{ color: PRIMARY }} />
                  </Box>
                ) : (
                  /* CHART CONTENT */
                  (() => {
                    const diagnosisList = diagnosisDataByPeriod[selectedPeriod2] || [];
                    const maxValue = Math.max(...diagnosisList.map((m) => m.value), 1);

                   return (
                  <>
                    {/* IF DATA AVAILABLE */}
                    {diagnosisList.length > 0 ? (
                      <Box sx={{ display: "flex", height: 300, gap: 2, position: "relative" }}>
                        {/* CATEGORY LABELS (Y-AXIS) */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            width: 250,
                            fontSize: "0.75rem",
                            py: 1
                          }}
                        >
                          {diagnosisList.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                pr: 1,
                                color: "text.secondary",
                                textAlign: "right"
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  bgcolor: chartColors[index % chartColors.length],
                                  mr: 1,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                                }}
                              />
                              <Typography variant="caption" fontWeight={500}>{item.label}</Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* CHART AREA */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            py: 1,
                            position: "relative"
                          }}
                        >
                          {/* HORIZONTAL GRID */}
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              flexDirection: "column"
                            }}
                          >
                            {diagnosisList.map((_, i) => (
                              <Box
                                key={i}
                                sx={{
                                  flex: 1,
                                  borderBottom: "1px solid #f0f0f0"
                                }}
                              />
                            ))}
                          </Box>

                          {/* VERTICAL GRID */}
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex"
                            }}
                          >
                            {Array.from({ length: 6 }).map((_, i) => (
                              <Box
                                key={i}
                                sx={{
                                  flex: 1,
                                  borderRight: i < 5 ? "1px solid #f0f0f0" : "none"
                                }}
                              />
                            ))}
                          </Box>

                          {/* BARS */}
                          {diagnosisList.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                position: "relative",
                                zIndex: 1
                              }}
                            >
                              <Box
                                sx={{
                                  height: "20px",
                                  bgcolor: chartColors[index % chartColors.length],
                                  width: `${(item.value / maxValue) * 100}%`,
                                  borderRadius: "5px",
                                  transition: "all 0.3s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  pr: 1,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  "&:hover": {
                                    transform: "scaleY(1.1)",
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                                  }
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "white", fontWeight: "bold", fontSize: "0.7rem" }}
                                >
                                  {item.value}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>

                        {/* X-AXIS SCALE */}
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -25,
                            left: 250,
                            right: 0,
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.75rem",
                            color: "text.secondary"
                          }}
                        >
                          <Typography variant="caption">0</Typography>
                          <Typography variant="caption">{Math.round(maxValue * 0.2)}</Typography>
                          <Typography variant="caption">{Math.round(maxValue * 0.4)}</Typography>
                          <Typography variant="caption">{Math.round(maxValue * 0.6)}</Typography>
                          <Typography variant="caption">{Math.round(maxValue * 0.8)}</Typography>
                          <Typography variant="caption">{maxValue}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: 200,
                          color: "text.secondary"
                        }}
                      >
                        <Typography>No data for {selectedPeriod2}</Typography>
                      </Box>
                    )}

                    {/* SUMMARY */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Summary for {getDateRangeText(selectedPeriod2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Conditions: {diagnosisList.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Diagnosed Count:{" "}
                        {diagnosisList.reduce((sum, item) => sum + item.value, 0)}
                      </Typography>
                    </Box>
                  </>
                );
                  })()
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} size={6}>
                    <Card elevation={0} sx={{ height: '100%', borderRadius: 2, bgcolor: '#fff', border: `1px solid ${BORDER}` }}>
                      <CardContent sx={{ p: 2.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 2.25, flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                              <TrendingUpIcon sx={{ color: PRIMARY, fontSize: 16 }} />
                              <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
                                Prescribing
                              </Typography>
                            </Stack>
                            <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: STRONG }}>
                              Top 10 Prescribed Medicines
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.25 }}>
                              {getDateRangeText()}
                            </Typography>
                          </Box>

                          {/* Filter Pills */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {filterPeriods.map((period) => {
                              const active = selectedPeriod === period;
                              return (
                                <Button
                                  key={period}
                                  onClick={() => handlePeriodChange(period)}
                                  disableElevation
                                  size="small"
                                  sx={{
                                    px: 1.25, py: 0.4,
                                    fontSize: '0.74rem', fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: active ? PRIMARY : BORDER,
                                    bgcolor: active ? PRIMARY : '#fff',
                                    color: active ? '#fff' : '#475569',
                                    '&:hover': { bgcolor: active ? PRIMARY_DARK : '#f1f5f9', borderColor: active ? PRIMARY_DARK : '#cbd5e1' },
                                  }}
                                >
                                  {period}
                                </Button>
                              );
                            })}
                          </Box>
                        </Box>
                        
                        {topMedications.length > 0 ? (
                          <Box sx={{ 
                            display: 'flex', 
                            height: 400,
                            gap: 2
                          }}>
                            {/* Y-axis labels */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              justifyContent: 'space-between',
                              paddingY: 1,
                              width: 350,
                              fontSize: '0.75rem'
                            }}>
                              {topMedications.map((med, index) => (
                                <Box key={index} sx={{ 
                                  height: '32px', 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  paddingRight: 1,
                                  color: 'text.secondary'
                                }}>
                                  <Typography variant="caption" sx={{ textAlign: 'right', lineHeight: 1.2 }}>
                                    {med.name}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
          
                            {/* Chart area */}
                            <Box sx={{ 
                              flex: 1, 
                              display: 'flex', 
                              flexDirection: 'column', 
                              justifyContent: 'space-between',
                              paddingY: 1,
                              position: 'relative'
                            }}>
                              {/* Grid lines */}
                              <Box sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                {Array.from({ length: Math.min(topMedications.length + 1, 11) }, (_, i) => (
                                  <Box key={i} sx={{ 
                                    flex: 1,
                                    borderBottom: i < Math.min(topMedications.length, 10) ? '1px solid #f0f0f0' : 'none'
                                  }} />
                                ))}
                              </Box>
          
                              {/* Vertical grid lines */}
                              <Box sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex'
                              }}>
                                {Array.from({ length: 6 }, (_, i) => (
                                  <Box key={i} sx={{ 
                                    flex: 1,
                                    borderRight: i < 5 ? '1px solid #f0f0f0' : 'none'
                                  }} />
                                ))}
                              </Box>
          
                              {/* Bars */}
                              {topMedications.map((med, index) => (
                                <Box key={index} sx={{ 
                                  height: '32px', 
                                  display: 'flex', 
                                  alignItems: 'center',
                                  position: 'relative',
                                  zIndex: 1
                                }}>
                                  <Box
                                    sx={{
                                      height: '20px',
                                      backgroundColor: med.color,
                                      width: `${(med.value / maxValuee) * 100}%`,
                                      borderRadius: '2px',
                                      position: 'relative',
                                      transition: 'all 0.3s ease-in-out',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      paddingRight: 1
                                    }}
                                  >
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        fontSize: '0.7rem',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                                      }}
                                    >
                                      {med.value}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
          
                            {/* X-axis values */}
                            <Box sx={{ 
                              position: 'absolute',
                              bottom: -25,
                              left: 350,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '0.75rem',
                              color: 'text.secondary'
                            }}>
                              <Typography variant="caption">0</Typography>
                              <Typography variant="caption">{Math.round(maxValuee * 0.2)}</Typography>
                              <Typography variant="caption">{Math.round(maxValuee * 0.4)}</Typography>
                              <Typography variant="caption">{Math.round(maxValuee * 0.6)}</Typography>
                              <Typography variant="caption">{Math.round(maxValuee * 0.8)}</Typography>
                              <Typography variant="caption">{maxValuee}</Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              height: 200,
                              color: 'text.secondary'
                            }}
                          >
                            <Typography variant="body1">
                              No prescription data for {selectedPeriod.toLowerCase()}
                            </Typography>
                          </Box>
                        )}
          
                        {/* Data Summary */}
                        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                            Data Summary for {selectedPeriod}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Period: {getDateRangeText()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Medications Tracked: {topMedications.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Quantity Dispensed: {topMedications.reduce((sum, med) => sum + med.value, 0)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
          </Grid>
        </Grid>
      </Box>

      <MedicalHistoryDialog
        open={medHistoryOpen}
        onClose={() => setMedHistoryOpen(false)}
        patientData={selectedPatient}
      />
    </Box>
  );
};

export default Home;